/**
 * TheNewsPulse.pro — Home page.
 *
 * Sections (top → bottom):
 *  1. Hero band            — dark navy globe, big headline, hero search,
 *                            "Today's Highlights" card.
 *  2. Category tile row    — 9 coloured shortcut tiles.
 *  3. Top Stories + Trending — featured card, secondary list, numbered
 *                            "Trending Now" list.
 *  4. Explore by Region    — 7 landmark cards.
 *  5. Latest News          — tab filter + 6-card grid.
 *  6. Network band         — sister-site chips + tagline.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, Globe2, Zap, ShieldCheck, Users, Flame, ChevronRight,
  Sprout, Plane, Landmark, Luggage, HeartPulse, Leaf, BarChart3,
  MoreHorizontal, MessageCircle,
} from "lucide-react";
import { npApi } from "./api";
import RegionChip from "./RegionChip";
import PublisherWidget from "@/components/publisher/PublisherWidget";
import {
  CATEGORY_TILES, REGIONS, NETWORK_SITES,
  timeAgo, primaryCategoryPath, categoryLabel,
} from "./theme";

const ICON_MAP = {
  Globe2, Sprout, Plane, Landmark, Luggage, HeartPulse, Leaf, BarChart3,
  MoreHorizontal, MessageCircle,
};

/** Category → gradient class used to tint image-less thumbnails. */
const CAT_GRADIENT = {
  aviation:    "from-sky-400 to-blue-600",
  agriculture: "from-emerald-400 to-green-600",
  travel:      "from-violet-400 to-purple-600",
  food:        "from-amber-400 to-orange-600",
  nature:      "from-green-500 to-emerald-700",
  health:      "from-rose-400 to-red-600",
  business:    "from-yellow-400 to-amber-600",
  world:       "from-blue-400 to-indigo-600",
  india:       "from-orange-400 to-red-500",
  government:  "from-slate-500 to-slate-700",
};

/** Category-coloured pill used above every headline. */
function CategoryPill({ cats }) {
  const label = categoryLabel(cats);
  if (!label) return null;
  return (
    <span className="text-[10px] font-bold uppercase tracking-widest text-blue-600">
      {label}
    </span>
  );
}

/** Small square thumbnail with a graceful gradient fallback tinted by category. */
function Thumb({ src, alt, cats, className = "" }) {
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return (
      <img
        src={src}
        alt={alt}
        className={`object-cover w-full h-full ${className}`}
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }
  const cat = primaryCategoryPath(cats);
  const grad = CAT_GRADIENT[cat] || "from-slate-200 to-slate-300";
  const Icon = cat === "aviation" ? Plane
    : cat === "agriculture" ? Sprout
    : cat === "travel" ? Luggage
    : cat === "health" ? HeartPulse
    : cat === "nature" ? Leaf
    : cat === "business" ? BarChart3
    : cat === "india" ? Landmark
    : Globe2;
  return (
    <div className={`w-full h-full bg-gradient-to-br ${grad} flex items-center justify-center ${className}`}>
      <Icon className="w-8 h-8 text-white/70" strokeWidth={1.4} />
    </div>
  );
}

/* ───────────────────────── Hero band ───────────────────────── */

function HeroBand({ highlights, onSearch, q, setQ }) {
  return (
    <section
      className="relative bg-gradient-to-b from-[#0A1F3A] to-[#061426] text-white overflow-hidden"
      data-testid="newspulse-hero"
    >
      {/* Globe illustration (SVG) */}
      <div
        className="absolute right-0 top-0 bottom-0 w-full lg:w-2/3 pointer-events-none opacity-90"
        aria-hidden
      >
        <img
          src="https://images.unsplash.com/photo-1614730321146-b6fa6a46bcb4?w=1400&h=800&fit=crop"
          alt=""
          className="w-full h-full object-cover object-right"
          style={{
            maskImage: "linear-gradient(to right, transparent 0%, black 40%, black 100%)",
            WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 40%, black 100%)",
          }}
        />
      </div>

      <div className="relative max-w-[1280px] mx-auto px-4 lg:px-6 py-12 lg:py-16 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left — headline */}
        <div className="lg:col-span-2 max-w-xl">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-300 mb-3">
            Global News. Local Impact.
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
            A More<br />Connected World
          </h1>
          <p className="mt-5 text-base lg:text-lg text-slate-200/90 leading-relaxed">
            Comprehensive. Trusted. Real-Time.<br />
            News that matters — from every corner of the globe.
          </p>

          <form
            onSubmit={onSearch}
            className="mt-6 flex items-center bg-white rounded-lg pl-4 pr-1 py-1 max-w-md shadow-lg"
            data-testid="newspulse-hero-search-form"
          >
            <Search className="w-4 h-4 text-slate-400 mr-2" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search news, topics, locations or people…"
              className="flex-1 bg-transparent border-0 outline-none text-sm text-slate-900 placeholder:text-slate-400 py-2"
              data-testid="newspulse-hero-search-input"
            />
            <button
              type="submit"
              className="rounded-md w-10 h-9 bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition-colors"
              aria-label="Search"
              data-testid="newspulse-hero-search-submit"
            >
              <Search className="w-4 h-4" />
            </button>
          </form>

          {/* Feature badges */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-xl">
            {[
              { icon: Globe2,      title: "Global Coverage" },
              { icon: Zap,         title: "Real-Time Updates" },
              { icon: ShieldCheck, title: "Trusted Sources" },
              { icon: Users,       title: "Powering the SocialPulse Network" },
            ].map((f) => (
              <div key={f.title} className="text-slate-100/85">
                <f.icon className="w-5 h-5 text-blue-300 mb-1.5" />
                <div className="text-[11px] leading-snug font-medium">{f.title}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — Today's Highlights card */}
        <TodaysHighlights events={highlights} />
      </div>
    </section>
  );
}

function TodaysHighlights({ events }) {
  const items = (events || []).slice(0, 4);
  return (
    <aside
      className="bg-white text-slate-900 rounded-xl shadow-2xl overflow-hidden lg:col-span-1"
      data-testid="newspulse-today-highlights"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <h3 className="text-sm font-bold text-slate-900">Today's Highlights</h3>
        <Link to="/what-changed" className="text-slate-400 hover:text-blue-600 transition-colors">
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
      <ul>
        {items.length === 0 && (
          <li className="p-5 text-sm text-slate-500">No highlights yet.</li>
        )}
        {items.map((e) => (
          <li key={e.event_id} className="border-b last:border-b-0 border-slate-100">
            <Link
              to={`/event/${e.event_id}`}
              className="flex gap-3 p-4 hover:bg-slate-50 transition-colors"
              data-testid={`newspulse-highlight-${e.event_id}`}
            >
              <div className="w-16 h-16 shrink-0 rounded-md overflow-hidden bg-slate-100">
                <Thumb src={e.image_url} alt="" cats={e.categories} />
              </div>
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-slate-900 leading-snug line-clamp-2">
                  {e.headline}
                </h4>
                <div className="mt-1 text-[11px] text-slate-500 flex items-center gap-1.5 flex-wrap">
                  <span>{timeAgo(e.last_updated_at)}</span>
                  <span className="text-slate-300">|</span>
                  <span className="capitalize">
                    {categoryLabel(e.categories).toLowerCase()}
                  </span>
                  <RegionChip event={e} />
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </aside>
  );
}

/* ─────────────────────── Category tiles ─────────────────────── */

function CategoryTiles() {
  return (
    <section
      className="max-w-[1280px] mx-auto px-4 lg:px-6 -mt-8 relative z-10"
      data-testid="newspulse-category-tiles"
    >
      <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3">
        {CATEGORY_TILES.map((t) => {
          const Icon = ICON_MAP[t.icon] || Globe2;
          const inner = (
            <div
              className={`aspect-square rounded-xl bg-gradient-to-br ${t.bg} text-white p-4 flex flex-col justify-between shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300`}
              data-testid={`newspulse-tile-${t.key}`}
            >
              <Icon className="w-6 h-6" />
              <div className="leading-tight">
                <div className="text-sm font-bold">{t.label}</div>
                <div className="text-xs opacity-90">{t.sub}</div>
              </div>
            </div>
          );
          return t.path ? (
            <Link key={t.key} to={`/topic/${t.path}`}>{inner}</Link>
          ) : (
            <div key={t.key}>{inner}</div>
          );
        })}
      </div>
    </section>
  );
}

/* ─────────────────────── Top Stories + Trending ─────────────────────── */

function TopStoriesBand({ latest }) {
  const featured = latest[0];
  const side = latest.slice(1, 5);
  const trending = latest.slice(0, 10);

  return (
    <section
      className="max-w-[1280px] mx-auto px-4 lg:px-6 mt-12"
      data-testid="newspulse-top-stories"
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Featured + side stories */}
        <div className="lg:col-span-2">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="text-2xl font-bold text-slate-900">Top Stories</h2>
            <Link
              to="/breaking"
              className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Featured big card */}
            {featured && (
              <Link
                to={`/event/${featured.event_id}`}
                className="group block rounded-xl overflow-hidden bg-white border border-slate-200 hover:shadow-lg transition-shadow"
                data-testid="newspulse-top-featured"
              >
                <div className="aspect-[4/3] bg-slate-100 relative">
                  <Thumb src={featured.image_url} alt="" cats={featured.categories} />
                  <span className="absolute top-3 left-3 bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded">
                    Top Story
                  </span>
                </div>
                <div className="p-4">
                  <CategoryPill cats={featured.categories} />
                  <h3 className="mt-1 text-lg font-bold leading-snug text-slate-900 group-hover:text-blue-700 transition-colors line-clamp-3">
                    {featured.headline}
                  </h3>
                  {featured.summary && (
                    <p className="mt-2 text-sm text-slate-600 line-clamp-2">
                      {featured.summary}
                    </p>
                  )}
                  <div className="mt-2 text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                    <span>{timeAgo(featured.last_updated_at)}</span>
                    <RegionChip event={featured} />
                  </div>
                </div>
              </Link>
            )}

            {/* Side story list */}
            <div className="space-y-4">
              {side.map((e) => (
                <Link
                  key={e.event_id}
                  to={`/event/${e.event_id}`}
                  className="group flex gap-3 border-b last:border-b-0 border-slate-100 pb-4 hover:bg-slate-50 -mx-2 px-2 py-2 rounded-lg transition-colors"
                  data-testid={`newspulse-top-side-${e.event_id}`}
                >
                  <div className="w-20 h-20 shrink-0 rounded-md overflow-hidden bg-slate-100">
                    <Thumb src={e.image_url} alt="" cats={e.categories} />
                  </div>
                  <div className="min-w-0">
                    <CategoryPill cats={e.categories} />
                    <h4 className="mt-0.5 text-sm font-semibold leading-snug text-slate-900 group-hover:text-blue-700 line-clamp-3">
                      {e.headline}
                    </h4>
                    <div className="mt-1 text-[11px] text-slate-500 flex items-center gap-1.5 flex-wrap">
                      <span>{timeAgo(e.last_updated_at)}</span>
                      <RegionChip event={e} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Trending Now */}
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-500" /> Trending Now
          </h2>
          <ol className="space-y-4">
            {trending.map((e, i) => (
              <li key={e.event_id} className="flex gap-3 items-start border-b last:border-b-0 border-slate-100 pb-4">
                <span className="text-slate-400 text-lg font-mono w-6 shrink-0 pt-0.5 tabular-nums">
                  {i + 1}
                </span>
                <Link
                  to={`/event/${e.event_id}`}
                  className="text-sm font-medium text-slate-800 hover:text-blue-700 leading-snug line-clamp-3"
                  data-testid={`newspulse-trending-${e.event_id}`}
                >
                  {e.headline}
                </Link>
              </li>
            ))}
            {trending.length === 0 && (
              <li className="text-sm text-slate-500">Trending stories will appear here.</li>
            )}
          </ol>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────── Explore News by Region ─────────────────────── */

function RegionBand() {
  return (
    <section
      className="max-w-[1280px] mx-auto px-4 lg:px-6 mt-14"
      data-testid="newspulse-region-band"
    >
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-2xl font-bold text-slate-900">Explore News by Region</h2>
        <Link
          to="/topic/world"
          className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          View All <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {REGIONS.map((r) => (
          <Link
            key={r.key}
            to={`/topic/world`}
            className="group block"
            data-testid={`newspulse-region-${r.key}`}
          >
            <div className="aspect-[4/3] rounded-xl overflow-hidden bg-slate-100 shadow-sm group-hover:shadow-lg transition-all group-hover:-translate-y-0.5">
              <img
                src={r.img}
                alt={r.label}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
                onError={(e) => {
                  // Graceful fallback: swap in a same-tone gradient block.
                  const el = e.currentTarget;
                  el.style.display = "none";
                  if (el.parentElement && !el.parentElement.dataset.fallback) {
                    el.parentElement.dataset.fallback = "1";
                    el.parentElement.className +=
                      " bg-gradient-to-br from-sky-500 to-blue-700 flex items-center justify-center text-white/80 font-semibold text-sm";
                    el.parentElement.textContent = r.label;
                  }
                }}
              />
            </div>
            <div className="mt-2 text-sm font-semibold text-slate-800 text-center">
              {r.label}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────── Latest News grid ─────────────────────── */

const LATEST_TABS = [
  { key: "latest",      label: "Latest",      path: null },
  { key: "india",       label: "India",       path: "india" },
  { key: "aviation",    label: "Aviation",    path: "aviation" },
  { key: "agriculture", label: "Agriculture", path: "agriculture" },
  { key: "travel",      label: "Travel",      path: "travel" },
  { key: "environment", label: "Environment", path: "nature" },
];

function LatestNewsBand({ latest }) {
  const [tab, setTab] = useState("latest");
  const [tabData, setTabData] = useState(null);
  const [loadingTab, setLoadingTab] = useState(false);

  useEffect(() => {
    if (tab === "latest") { setTabData(null); return; }
    const t = LATEST_TABS.find((x) => x.key === tab);
    if (!t?.path) return;
    let cancelled = false;
    setLoadingTab(true);
    npApi.byCategory(t.path, { limit: 6 })
      .then((rows) => { if (!cancelled) setTabData(rows); })
      .catch(() => { if (!cancelled) setTabData([]); })
      .finally(() => { if (!cancelled) setLoadingTab(false); });
    return () => { cancelled = true; };
  }, [tab]);

  const items = (tab === "latest" ? latest : (tabData || [])).slice(0, 6);

  return (
    <section
      className="max-w-[1280px] mx-auto px-4 lg:px-6 mt-14"
      data-testid="newspulse-latest-band"
    >
      <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
        <h2 className="text-2xl font-bold text-slate-900">Latest News</h2>
        <div className="flex items-center gap-4 overflow-x-auto text-sm">
          {LATEST_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`whitespace-nowrap py-1 border-b-2 transition-colors ${
                tab === t.key
                  ? "border-blue-600 text-blue-600 font-semibold"
                  : "border-transparent text-slate-600 hover:text-slate-900"
              }`}
              data-testid={`newspulse-latest-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
          <Link
            to="/what-changed"
            className="text-blue-600 hover:text-blue-700 flex items-center gap-1 font-medium"
          >
            View All <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {loadingTab
          ? [...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[4/3] rounded-lg bg-slate-200" />
                <div className="h-3 w-1/2 bg-slate-200 rounded mt-2" />
                <div className="h-4 w-full bg-slate-200 rounded mt-2" />
              </div>
            ))
          : items.map((e) => (
              <Link
                key={e.event_id}
                to={`/event/${e.event_id}`}
                className="group block"
                data-testid={`newspulse-latest-card-${e.event_id}`}
              >
                <div className="aspect-[4/3] rounded-lg overflow-hidden bg-slate-100">
                  <Thumb src={e.image_url} alt="" cats={e.categories} />
                </div>
                <div className="mt-2">
                  <CategoryPill cats={e.categories} />
                  <h3 className="mt-0.5 text-sm font-semibold text-slate-900 group-hover:text-blue-700 leading-snug line-clamp-3">
                    {e.headline}
                  </h3>
                  <div className="mt-1 text-[11px] text-slate-500 flex items-center gap-1.5 flex-wrap">
                    <span>{timeAgo(e.last_updated_at)}</span>
                    <RegionChip event={e} />
                  </div>
                </div>
              </Link>
            ))}
        {!loadingTab && items.length === 0 && (
          <div className="col-span-full text-sm text-slate-500 py-8 text-center">
            No stories in this category yet.
          </div>
        )}
      </div>
    </section>
  );
}

/* ─────────────────────── Network band ─────────────────────── */

function NetworkBand() {
  return (
    <section
      className="max-w-[1280px] mx-auto px-4 lg:px-6 mt-14 mb-8"
      data-testid="newspulse-network-band"
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
        <div className="lg:col-span-2 bg-slate-50 rounded-2xl p-6 border border-slate-200">
          <h3 className="text-lg font-bold text-slate-900">
            Powering a Network of Informed Communities
          </h3>
          <p className="mt-1 text-sm text-slate-600 max-w-lg">
            NewsPulse delivers trusted news to all SocialPulse network sites —
            from global topics to local communities.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {NETWORK_SITES.map((s) => {
              const Icon = ICON_MAP[s.icon] || Globe2;
              return (
                <a
                  key={s.key}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 bg-white border border-slate-200 hover:border-blue-400 hover:shadow-sm rounded-full pl-2 pr-4 py-1.5 transition-all"
                  data-testid={`newspulse-network-${s.key}`}
                >
                  <Icon className={`w-4 h-4 ${s.color}`} />
                  <span className="text-sm font-medium text-slate-800">{s.label}</span>
                </a>
              );
            })}
            <span className="text-xs text-slate-500 border border-slate-300 rounded-full px-3 py-1.5">
              + Many More
            </span>
          </div>
        </div>
        <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-white/70 flex items-center justify-center shrink-0">
            <Users className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <div className="text-base font-semibold text-slate-900 leading-snug">
              Real News.<br />Real Communities.<br />A Better Tomorrow.
            </div>
          </div>
          <ChevronRight className="ml-auto w-5 h-5 text-blue-500 shrink-0" />
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────────── Page ───────────────────────────── */

export default function NewsPulseHome() {
  const [latest, setLatest] = useState([]);
  const [breaking, setBreaking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [l, b] = await Promise.all([
          npApi.latest({ limit: 40 }),
          npApi.breaking(6).catch(() => []),
        ]);
        if (cancelled) return;
        setLatest(l);
        setBreaking(b);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const highlights = useMemo(() => {
    // Prefer breaking events; fall back to latest.
    const seed = breaking?.length ? breaking : latest;
    return seed.slice(0, 4);
  }, [breaking, latest]);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-500" data-testid="newspulse-loading">
        Loading TheNewsPulse…
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-red-600" data-testid="newspulse-error">
        Error: {err}
      </div>
    );
  }

  return (
    <div data-testid="newspulse-home">
      <HeroBand highlights={highlights} q={q} setQ={setQ} onSearch={onSearch} />
      <CategoryTiles />
      <TopStoriesBand latest={latest} />
      <RegionBand />
      <LatestNewsBand latest={latest} />
      {/* Articles & Blogs — canonical long-form from SocialPulse Publisher.
          Rendered as a separate section so wire-copy cards and analysis
          pieces don't visually blend. Silent when nothing is distributed. */}
      <section className="bg-slate-50 border-y border-slate-200 py-8">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
          <PublisherWidget
            siteId="newspulse"
            title="Articles & Blogs"
            limit={6}
            testIdPrefix="newspulse-home-publisher"
          />
        </div>
      </section>
      <NetworkBand />
    </div>
  );
}
